/* eslint-disable no-else-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { webAnalytics as wa } from '@principal/web-analytics';
import React, { useState } from 'react';
import moment from 'moment';
import ToggleDropdownSelector from '../../ToggleDropdownSelector/ToggleDropdownSelector';
import AdvancedSearchCriteria from '../../models/AdvancedSearchCriteria';
import AdvancedSearchOption from '../../models/AdvancedSearchOption';
import '../Search/Search.css';
import DatePicker from '../../DatePicker/DatePicker';
import {
  useAdvancedSearchCriteria,
  useAdvancedSearchCriteriaUpdater,
  useAdvancedSearchOptions,
  useTriggerAdvancedSearch,
} from '../../hooks/useAdvancedSearchCriteria';
import '../SavedSearchDropDown/SavedSearchMenu.css';
import SavedSearchDropDown from '../SavedSearchDropDown/SavedSearchDropDown';
import SearchPills from '../SearchPills/SearchPills';
import { useHubTab } from '../../../shared/hooks/useHubTab';
import { SearchType } from '../../../shared/models/WebAnalytics';
import { getSelectedAdvancedSearchFilterLabels } from '../../../util/analyticsEventHelpers';

const formatDateIso = (date: Date | undefined) => moment(date).format('YYYY-MM-DD');
export const ALL_LINES_OF_COVERAGE: { label: string; value: string }[] = [
  { label: 'Workplace Benefits', value: 'Workplace Benefits' },
  { label: 'Life', value: 'Life' },
  { label: 'Disability Insurance', value: 'Disability Insurance' },
  { label: 'Annuity', value: 'Annuity' },
];

const AdvancedSearchInputGroup = () => {
  const [searchInputText, setSearchInputText] = useState<string>();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDateText, setStartDateText] = useState<string>();
  const [endDateText, setEndDateText] = useState<string>();
  const [lineOfCoverage, setLineOfCoverage] = useState<string>(ALL_LINES_OF_COVERAGE[0].value);
  const [currentField, setCurrentField] = useState<AdvancedSearchOption>();

  const searchCriteria: AdvancedSearchCriteria | undefined = useAdvancedSearchCriteria();
  const updateSearchCriteria = useAdvancedSearchCriteriaUpdater();
  const searchOptions = useAdvancedSearchOptions();
  const { toggleTriggerAdvancedSearch } = useTriggerAdvancedSearch();
  const hubTab = useHubTab();

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputText(event.target.value);
  };

  const onDateChange = (newStartDate: Date | undefined, newEndDate: Date | undefined) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    const newStartDateText = formatDateIso(newStartDate);
    const newEndDateText = formatDateIso(newEndDate);
    setStartDateText(newStartDateText);
    setEndDateText(newEndDateText);
    setSearchInputText(`${newStartDate} - ${newEndDate}`);
  };

  const onSelectChange = (value: string) => {
    setLineOfCoverage(value);
  };

  const addFilter = (event: React.FormEvent) => {
    event.preventDefault();
    const key = currentField?.key;
    const label = currentField?.label;

    if ((searchInputText || key === 'lineOfCoverage') && key && label) {
      const newSearchCriteria: AdvancedSearchCriteria = { ...searchCriteria };

      if (key === 'paidDate') {
        newSearchCriteria.startDate = startDateText;
        newSearchCriteria.endDate = endDateText;
      } else if (key === 'lineOfCoverage') {
        const selectedLinesOfCoverage: string[] = (searchCriteria as any)[key] || [];
        if (lineOfCoverage && !selectedLinesOfCoverage.includes(lineOfCoverage)) {
          (newSearchCriteria as any)[key] = [...selectedLinesOfCoverage, lineOfCoverage];
        }
      } else {
        const previousSearchValue: string[] = (searchCriteria as any)[key] || [];
        if (searchInputText && !previousSearchValue.includes(searchInputText)) {
          (newSearchCriteria as any)[key] = [...previousSearchValue, searchInputText.trim()];
        }
      }

      setSearchInputText('');
      setStartDate(undefined);
      setEndDate(undefined);
      updateSearchCriteria(newSearchCriteria);
      wa.trackEvent('pageNavigationClick', {
        clicktext: `${hubTab}-${SearchType.ADVANCED_SEARCH}-Add Filter-${label}`,
        navigationsection: 'filter',
      });
    }
  };

  const performSearch = (event: React.FormEvent) => {
    event.preventDefault();

    // If user types in the current field but hasn't yet been added it to the search criteria,
    // automatically add it as a search criteria, and then submit the Search.
    const key = currentField?.key;
    let pendingKey: string | undefined;

    if (key && searchInputText) {
      const searchCriteriaArray: string[] = (searchCriteria as any)[key] || [];
      if (!searchCriteriaArray.includes(searchInputText)) {
        pendingKey = key;
        addFilter(event);
      }
      setSearchInputText('');
      setStartDate(undefined);
      setEndDate(undefined);
    }

    const selectedFilters = getSelectedAdvancedSearchFilterLabels(searchCriteria, searchOptions, pendingKey);

    toggleTriggerAdvancedSearch(true);
    if (selectedFilters) {
      wa.trackEvent('formSubmit', {
        name: `${hubTab}-${SearchType.ADVANCED_SEARCH}-Search-${selectedFilters}`,
        type: '',
        lead: 'no',
        id: '',
      });
    }
  };

  const displayAdvancedSearchInputType = (currentFieldKey: string | undefined) => {
    if (currentFieldKey === 'lineOfCoverage') {
      return (
        <select
          name="lineOfCoverage"
          id="lineOfCoverage"
          className="form-control line-of-coverage"
          onChange={(e) => onSelectChange(e.target.value)}
        >
          {ALL_LINES_OF_COVERAGE.map((loc) => (
            <option value={loc.value}>{loc.label}</option>
          ))}
        </select>
      );
    } else if (currentFieldKey === 'paidDate') {
      return (
        <DatePicker
          startDate={startDate}
          endDate={endDate}
          onChange={onDateChange}
          isDisabled={searchCriteria?.startDate !== undefined && searchCriteria.endDate !== undefined}
        />
      );
    } else {
      return (
        <input
          id="search"
          placeholder={placeholder}
          type="text"
          value={searchInputText || ''}
          className="form-control"
          onChange={onSearchInputChange}
        />
      );
    }
  };

  const placeholder = `Filter on: ${currentField?.label}`;
  return (
    <>
      <form onSubmit={addFilter} className="input-suffix">
        <div className="row">
          <div className="col-md-10">
            <div className="input-group">
              {searchOptions && <ToggleDropdownSelector options={searchOptions} onChange={setCurrentField} />}
              {displayAdvancedSearchInputType(currentField?.key)}
              {currentField?.key !== 'lineOfCoverage' && (
                <span className="suffix magnify">
                  <i className="fa fa-search text-primary" />
                </span>
              )}
            </div>
          </div>
          <div className="col-md-2">
            <div className="btn-group">
              <button type="button" className="btn btn-primary apply-filter" onClick={addFilter}>
                Add Filter
              </button>
              <SavedSearchDropDown />
            </div>
          </div>
        </div>
        <SearchPills />
      </form>
      <button className="btn btn-primary" type="submit" onClick={performSearch}>
        Search
      </button>
    </>
  );
};

export default AdvancedSearchInputGroup;
